import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
    const { language } = useLanguage();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (password !== confirmPassword) {
            setError(language === 'zh' ? '密碼不匹配' : 'Passwords do not match');
            return;
        }
        
        if (password.length < 6) {
            setError(language === 'zh' ? '密碼至少需要6個字符' : 'Password must be at least 6 characters');
            return;
        }
        
        setLoading(true);
        
        try {
            const response = await fetch(`${API_URL}/api/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, new_password: password })
            });
            
            const data = await response.json();
            
            if (response.ok) {
                setSuccess(true);
            } else {
                setError(data.detail || 'Failed to reset password');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        }
        
        setLoading(false);
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 px-4 py-12">
                <div className="w-full max-w-md animate-fade-in">
                    <Card className="border-2">
                        <CardContent className="pt-8 pb-8 text-center">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <XCircle className="w-8 h-8 text-red-600" />
                            </div>
                            <h2 className="text-xl font-semibold mb-2">
                                {language === 'zh' ? '無效的連結' : 'Invalid Link'}
                            </h2>
                            <p className="text-muted-foreground mb-6">
                                {language === 'zh' 
                                    ? '此密碼重置連結無效或已過期'
                                    : 'This password reset link is invalid or has expired'
                                }
                            </p>
                            <Link to="/forgot-password">
                                <Button className="w-full rounded-full">
                                    {language === 'zh' ? '請求新連結' : 'Request New Link'}
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 px-4 py-12">
                <div className="w-full max-w-md animate-fade-in">
                    <Card className="border-2">
                        <CardContent className="pt-8 pb-8 text-center">
                            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-emerald-600" />
                            </div>
                            <h2 className="text-xl font-semibold mb-2">
                                {language === 'zh' ? '密碼已重置' : 'Password Reset'}
                            </h2>
                            <p className="text-muted-foreground mb-6">
                                {language === 'zh' 
                                    ? '您的密碼已成功重置，現在可以使用新密碼登入'
                                    : 'Your password has been reset successfully. You can now log in with your new password.'
                                }
                            </p>
                            <Button onClick={() => navigate('/login')} className="w-full rounded-full">
                                {language === 'zh' ? '前往登入' : 'Go to Login'}
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 px-4 py-12">
            <div className="w-full max-w-md animate-fade-in">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-block">
                        <h1 className="font-serif text-2xl font-bold">
                            <span className="font-chinese text-wealth">通书</span> Plotter
                        </h1>
                    </Link>
                </div>
                
                <Card className="border-2">
                    <CardHeader className="space-y-1">
                        <CardTitle className="font-serif text-2xl">
                            {language === 'zh' ? '重置密碼' : 'Reset Password'}
                        </CardTitle>
                        <CardDescription>
                            {language === 'zh' 
                                ? '請輸入您的新密碼'
                                : 'Enter your new password below'
                            }
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                                    {error}
                                </div>
                            )}
                            
                            <div className="space-y-2">
                                <Label htmlFor="password">
                                    {language === 'zh' ? '新密碼' : 'New Password'}
                                </Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={6}
                                        data-testid="reset-password-input"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {language === 'zh' ? '至少6個字符' : 'At least 6 characters'}
                                </p>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">
                                    {language === 'zh' ? '確認密碼' : 'Confirm Password'}
                                </Label>
                                <Input
                                    id="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    data-testid="reset-confirm-password-input"
                                />
                            </div>
                            
                            <Button 
                                type="submit" 
                                className="w-full rounded-full" 
                                disabled={loading}
                                data-testid="reset-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {language === 'zh' ? '重置中...' : 'Resetting...'}
                                    </>
                                ) : (
                                    language === 'zh' ? '重置密碼' : 'Reset Password'
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ResetPassword;
